import { useState, useEffect, useCallback, useRef } from "react";
import { X, ChevronLeft, ChevronRight, Eye, Trash2, Plus, Camera } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

// Story Progress Bar Component
const StoryProgress = ({ total, current, progress }) => {
  return (
    <div className="story-progress-container">
      {[...Array(total)].map((_, i) => (
        <div key={i} className="story-progress-bar">
          <div 
            className="story-progress-fill"
            style={{ 
              width: i < current ? '100%' : i === current ? `${progress}%` : '0%' 
            }}
          />
        </div>
      ))}
    </div>
  );
};

// Story Viewer Component
export const StoryViewer = ({ 
  isOpen, 
  onClose, 
  userStories, // Array of user story groups
  initialUserIndex = 0,
  currentUser,
  onStoryDeleted,
  onAddStory // New prop - callback to open create story modal
}) => {
  const [userIndex, setUserIndex] = useState(initialUserIndex);
  const [storyIndex, setStoryIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [showViewers, setShowViewers] = useState(false);
  const [viewers, setViewers] = useState([]);
  const timerRef = useRef(null);
  const progressRef = useRef(null);

  const STORY_DURATION = 5000; // 5 seconds per story

  const currentUserStories = userStories[userIndex];
  const currentStory = currentUserStories?.stories?.[storyIndex];
  const isOwnStory = currentUserStories?.user_id === currentUser?.user_id;

  // Mark story as viewed
  const markAsViewed = useCallback(async (storyId) => {
    try {
      await axios.post(`${API}/stories/${storyId}/view`);
    } catch (error) {
      console.error("Failed to mark story as viewed:", error);
    }
  }, []);

  // Navigate to next story/user
  const goNext = useCallback(() => {
    if (storyIndex < currentUserStories.stories.length - 1) {
      setStoryIndex(prev => prev + 1);
      setProgress(0);
    } else if (userIndex < userStories.length - 1) {
      setUserIndex(prev => prev + 1);
      setStoryIndex(0);
      setProgress(0);
    } else {
      onClose();
    }
  }, [storyIndex, userIndex, currentUserStories?.stories?.length, userStories.length, onClose]);

  // Navigate to previous story/user
  const goPrevious = useCallback(() => {
    if (storyIndex > 0) {
      setStoryIndex(prev => prev - 1);
      setProgress(0);
    } else if (userIndex > 0) {
      setUserIndex(prev => prev - 1);
      const prevUserStories = userStories[userIndex - 1];
      setStoryIndex(prevUserStories.stories.length - 1);
      setProgress(0);
    }
  }, [storyIndex, userIndex, userStories]);

  // Progress timer
  useEffect(() => {
    if (!isOpen || isPaused || !currentStory) return;

    // Mark as viewed
    if (!currentStory.is_viewed && !isOwnStory) {
      markAsViewed(currentStory.story_id);
    }

    // Start progress timer
    const startTime = Date.now();
    const tick = () => {
      const elapsed = Date.now() - startTime;
      const newProgress = (elapsed / STORY_DURATION) * 100;
      
      if (newProgress >= 100) {
        goNext();
      } else {
        setProgress(newProgress);
        progressRef.current = requestAnimationFrame(tick);
      }
    };
    
    progressRef.current = requestAnimationFrame(tick);

    return () => {
      if (progressRef.current) {
        cancelAnimationFrame(progressRef.current);
      }
    };
  }, [isOpen, isPaused, currentStory, storyIndex, userIndex, goNext, markAsViewed, isOwnStory]);

  // Fetch viewers for own story
  const fetchViewers = useCallback(async () => {
    if (!currentStory || !isOwnStory) return;
    try {
      const response = await axios.get(`${API}/stories/${currentStory.story_id}/viewers`);
      setViewers(response.data);
    } catch (error) {
      console.error("Failed to fetch viewers:", error);
    }
  }, [currentStory, isOwnStory]);

  // Delete story
  const handleDeleteStory = async () => {
    if (!currentStory) return;
    try {
      await axios.delete(`${API}/stories/${currentStory.story_id}`);
      toast.success("Story deleted");
      onStoryDeleted?.(currentStory.story_id);
      
      // Navigate to next story or close
      if (currentUserStories.stories.length <= 1) {
        onClose();
      } else {
        goNext();
      }
    } catch (error) {
      toast.error("Failed to delete story");
    }
  };

  // Handle touch/click for navigation
  const handleTap = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const third = rect.width / 3;

    if (x < third) {
      goPrevious();
    } else if (x > third * 2) {
      goNext();
    }
  };

  // Format time remaining
  const formatTimeRemaining = (expiresAt) => {
    const now = new Date();
    const expires = new Date(expiresAt);
    const diffMs = expires - now;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    
    if (diffHours > 0) return `${diffHours}h left`;
    if (diffMins > 0) return `${diffMins}m left`;
    return "Expiring soon";
  };

  if (!isOpen || !currentStory) return null;

  return (
    <div className="story-viewer" onClick={handleTap}>
      {/* Progress Bars */}
      <StoryProgress 
        total={currentUserStories.stories.length} 
        current={storyIndex} 
        progress={progress} 
      />

      {/* Header */}
      <div 
        className="story-viewer-header"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3">
          <Avatar className="w-10 h-10 ring-2 ring-white/30">
            <AvatarImage src={currentUserStories.user_picture} />
            <AvatarFallback className="bg-[var(--ps-bg-elevated)] text-[var(--ps-primary)]">
              {currentUserStories.user_name?.charAt(0) || 'U'}
            </AvatarFallback>
          </Avatar>
          <div>
            <p className="font-semibold text-white text-sm">{currentUserStories.user_name}</p>
            <p className="text-xs text-white/60">{formatTimeRemaining(currentStory.expires_at)}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isOwnStory && (
            <>
              {/* Add Story button */}
              <button
                onClick={(e) => { 
                  e.stopPropagation(); 
                  onClose(); 
                  onAddStory?.(); 
                }}
                className="p-2 rounded-full hover:bg-white/10 text-[var(--ps-primary)]"
                title="Add Story"
              >
                <Plus className="w-5 h-5" />
              </button>
              <button
                onClick={() => {
                  setShowViewers(true);
                  fetchViewers();
                }}
                className="p-2 rounded-full hover:bg-white/10 text-white/80"
              >
                <Eye className="w-5 h-5" />
                <span className="text-xs ml-1">{currentStory.view_count || 0}</span>
              </button>
              <button
                onClick={handleDeleteStory}
                className="p-2 rounded-full hover:bg-white/10 text-red-400"
              >
                <Trash2 className="w-5 h-5" />
              </button>
            </>
          )}
          <button
            onClick={(e) => { e.stopPropagation(); onClose(); }}
            className="p-2 rounded-full hover:bg-white/10 text-white"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
      </div>

      {/* Story Content */}
      <div 
        className="story-content"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        {currentStory.image_url ? (
          <img 
            src={currentStory.image_url} 
            alt="Story" 
            className="max-w-full max-h-full object-contain"
            onError={(e) => {
              console.error('Story image failed to load:', currentStory.image_url);
              e.target.style.display = 'none';
            }}
          />
        ) : (
          /* Text-only story - display centered text on gradient background */
          <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-[var(--ps-primary)] to-emerald-700">
            {currentStory.content && (
              <p className="text-white text-2xl font-bold text-center px-8 max-w-lg">
                {currentStory.content}
              </p>
            )}
          </div>
        )}
        {/* Caption overlay for image stories */}
        {currentStory.image_url && currentStory.content && (
          <p className="absolute bottom-20 left-4 right-4 text-white text-lg font-medium text-center bg-black/30 p-4 rounded-xl">
            {currentStory.content}
          </p>
        )}
      </div>

      {/* Navigation Arrows (Desktop) */}
      <button
        onClick={(e) => { e.stopPropagation(); goPrevious(); }}
        className="absolute left-4 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/30 text-white hover:bg-black/50 hidden md:block"
        disabled={userIndex === 0 && storyIndex === 0}
      >
        <ChevronLeft className="w-6 h-6" />
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); goNext(); }}
        className="absolute right-4 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/30 text-white hover:bg-black/50 hidden md:block"
      >
        <ChevronRight className="w-6 h-6" />
      </button>

      {/* Viewers Modal */}
      {showViewers && (
        <div 
          className="absolute bottom-0 left-0 right-0 bg-[var(--ps-bg-surface)] rounded-t-2xl p-4 max-h-[50vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-white">Viewers ({viewers.length})</h3>
            <button onClick={() => setShowViewers(false)} className="text-white/60">
              <X className="w-5 h-5" />
            </button>
          </div>
          {viewers.length === 0 ? (
            <p className="text-center text-white/60 py-4">No viewers yet</p>
          ) : (
            <div className="space-y-3">
              {viewers.map((viewer) => (
                <div key={viewer.user_id} className="flex items-center gap-3">
                  <Avatar className="w-10 h-10">
                    <AvatarImage src={viewer.picture} />
                    <AvatarFallback className="bg-[var(--ps-bg-elevated)] text-[var(--ps-primary)]">
                      {viewer.name?.charAt(0) || 'U'}
                    </AvatarFallback>
                  </Avatar>
                  <span className="text-white">{viewer.name}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Create Story Modal
export const CreateStoryModal = ({ isOpen, onClose, user, onStoryCreated }) => {
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [caption, setCaption] = useState('');
  const [compressing, setCompressing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  // Compress image
  const compressImage = (file, maxSizeKB = 500) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new window.Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          const MAX_WIDTH = 1080;
          const MAX_HEIGHT = 1920;
          
          if (width > MAX_WIDTH) {
            height = (height * MAX_WIDTH) / width;
            width = MAX_WIDTH;
          }
          if (height > MAX_HEIGHT) {
            width = (width * MAX_HEIGHT) / height;
            height = MAX_HEIGHT;
          }
          
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          let quality = 0.85;
          let dataUrl = canvas.toDataURL('image/jpeg', quality);
          while (dataUrl.length > maxSizeKB * 1024 * 1.37 && quality > 0.1) {
            quality -= 0.1;
            dataUrl = canvas.toDataURL('image/jpeg', quality);
          }
          resolve(dataUrl);
        };
        img.onerror = reject;
        img.src = e.target.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleImageSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error("Please select an image");
      return;
    }
    
    setCompressing(true);
    try {
      const compressed = await compressImage(file);
      setImage(compressed);
      setImagePreview(compressed);
    } catch {
      toast.error("Failed to process image");
    } finally {
      setCompressing(false);
    }
  };

  const handleSubmit = async () => {
    if (!image) {
      toast.error("Please add an image to your story");
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/stories`, {
        content: caption || null,
        image_url: image
      });
      toast.success("Story shared!");
      onStoryCreated?.(response.data);
      handleClose();
    } catch (error) {
      toast.error("Failed to share story");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setImage(null);
    setImagePreview(null);
    setCaption('');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="bg-[var(--ps-bg-surface)] border-[var(--ps-border)] text-[var(--ps-text-primary)] max-w-md">
        <DialogHeader>
          <DialogTitle className="text-lg font-bold">Create Story</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* Image Upload Area */}
          {!imagePreview ? (
            <div className="space-y-3">
              {/* Camera and Gallery buttons */}
              <div className="flex gap-3">
                {/* Take Photo */}
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="flex-1 aspect-[9/16] max-h-[200px] rounded-xl border-2 border-dashed border-[var(--ps-border)] flex flex-col items-center justify-center cursor-pointer hover:border-[var(--ps-primary)] transition-colors"
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={handleImageSelect}
                    className="hidden"
                  />
                  {compressing ? (
                    <div className="dark-spinner" />
                  ) : (
                    <>
                      <Camera className="w-10 h-10 text-[var(--ps-primary)] mb-2" />
                      <p className="text-sm text-[var(--ps-text-secondary)]">Take Photo</p>
                    </>
                  )}
                </button>
                
                {/* Gallery */}
                <button
                  onClick={() => {
                    // Create temporary input for gallery
                    const input = document.createElement('input');
                    input.type = 'file';
                    input.accept = 'image/*';
                    input.onchange = handleImageSelect;
                    input.click();
                  }}
                  className="flex-1 aspect-[9/16] max-h-[200px] rounded-xl border-2 border-dashed border-[var(--ps-border)] flex flex-col items-center justify-center cursor-pointer hover:border-[var(--ps-primary)] transition-colors"
                >
                  <Plus className="w-10 h-10 text-[var(--ps-text-muted)] mb-2" />
                  <p className="text-sm text-[var(--ps-text-secondary)]">Gallery</p>
                </button>
              </div>
              
              {/* Text-only story option */}
              <div className="text-center">
                <p className="text-xs text-[var(--ps-text-muted)]">or create a text-only story</p>
              </div>
            </div>
          ) : (
            <div className="relative aspect-[9/16] max-h-[400px] rounded-xl overflow-hidden">
              <img 
                src={imagePreview} 
                alt="Story preview" 
                className="w-full h-full object-cover"
              />
              <button
                onClick={() => { setImage(null); setImagePreview(null); }}
                className="absolute top-2 right-2 bg-black/60 hover:bg-black/80 text-white rounded-full p-1.5"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Caption */}
          {imagePreview && (
            <Textarea
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder="Add a caption (optional)"
              className="bg-[var(--ps-bg-base)] border-[var(--ps-border)] text-[var(--ps-text-primary)] placeholder:text-[var(--ps-text-muted)] resize-none"
              maxLength={200}
            />
          )}
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={handleClose}
            className="text-[var(--ps-text-secondary)]"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!image || submitting}
            className="dark-btn-primary"
          >
            {submitting ? (
              <div className="dark-spinner w-4 h-4" />
            ) : (
              'Share Story'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default StoryViewer;
